import { Stack, Theme } from '@mui/material';
import { Controller } from 'react-hook-form';
import { FormAutocompleteProps, FormLabel, Tooltip } from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { SidePanelRow } from 'src/components/SidePanelRow';
import {
  ServiceLevelFormValue,
  useServiceLevelSelectMenuOptions,
} from 'src/components/SidePanelTabs/service-level-select-menu-options';
import { ServiceLevelOption } from 'src/graphql';
import { toSxArray } from 'src/utils/sx';

type SidePanelServiceLevelField = FormAutocompleteProps & { disabledTooltip?: string };

export const ServiceLevelSelectMenuField = ({
  field,
  serviceLevelOptions,
  cppExpDate,
  disabled,
}: {
  field: SidePanelServiceLevelField;
  serviceLevelOptions: ServiceLevelOption[];
  cppExpDate?: string | null;
  disabled?: boolean;
}) => {
  const selectMenuOptions = useServiceLevelSelectMenuOptions({
    serviceLevelOptions,
    currentServiceLevel: (field.defaultValue ?? null) as ServiceLevelFormValue | null,
    cppExpDate,
  });

  return (
    <SidePanelRow key={field.label}>
      <FormLabel label={field.label} />
      <Stack direction="row" width="100%" gap={1}>
        <Tooltip
          title={field.disabled && field.disabledTooltip ? field.disabledTooltip : ''}
          placement="bottom-start"
        >
          <span style={{ width: '100%' }}>
            <Controller
              name={field.name}
              rules={{ required: field.required }}
              render={({ field: controllerField }) => {
                const value = (controllerField.value ??
                  field.defaultValue ??
                  null) as ServiceLevelFormValue | null;

                return (
                  <SelectMenu
                    label={field.label ?? 'Service Level'}
                    options={selectMenuOptions}
                    value={getSelectedServiceLevelValue(value, selectMenuOptions)}
                    onChange={(value) => {
                      const selectedOption = serviceLevelOptions.find(
                        (option) => option.id === value,
                      );

                      if (selectedOption) {
                        controllerField.onChange(selectedOption);
                      }
                    }}
                    disabled={Boolean(disabled || field.disabled)}
                    searchPlaceholder="Search service levels"
                    emptyText={
                      field.loading ? 'Loading service levels...' : 'No service levels found.'
                    }
                    variant="inline"
                    plainOptionIcons
                    sx={[
                      { width: '100%' },
                      ...toSxArray(field.sx),
                      (theme: Theme) => ({
                        [theme.breakpoints.down('sm')]: {
                          fontSize: theme.utils.important('1rem'),
                        },
                      }),
                    ]}
                  />
                );
              }}
            />
          </span>
        </Tooltip>
      </Stack>
    </SidePanelRow>
  );
};

// Options are keyed by service level id, except the current-value fallback the
// options hook prepends, whose id can be a bare label (pre-LITS placeholder).
function getSelectedServiceLevelValue(
  value: ServiceLevelFormValue | null,
  options: SelectMenuOption[],
) {
  if (!value?.id) {
    return null;
  }

  return (
    options.find((option) => option.value === value.id || option.title === value.label)
      ?.value ?? null
  );
}
